import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Web3Service } from '@/services/web3.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import {HistoryItem} from "@/models/search";
import { ThemeService } from '@/services/theme.service';
import { PlatformService } from '@/services/platform.service';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss'
})

export class SearchComponent {

  @ViewChild('searchInput') searchInput!: ElementRef;

  phunkBoxLoading: boolean = false;
  phunkBoxError: boolean = false;
  searchHistory: HistoryItem[] = [];

  
  phunkBox: FormGroup = new FormGroup({
    addressInput: new FormControl()
  });
  theme: string = 'light';
  searchHistoryActive: boolean = false;

  constructor(
    private router: Router,
    private themeSvc: ThemeService,
    private web3Svc: Web3Service,
    private platformService: PlatformService
  ) {
    firstValueFrom(this.themeSvc.theme$).then(theme => {
      this.theme = theme;
    });
  }

  ngOnInit(): void {
    const searchHistoryStr = this.platformService.getLocalStorageItem('searchHistory');
    this.searchHistory = searchHistoryStr ? JSON.parse(searchHistoryStr) : [];
    this.themeSvc.theme$.subscribe((theme: string) => {
      console.log('theme', theme);
      this.theme = theme;
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  keydownHandler($event: KeyboardEvent) {
    this.unfocusInput();
  }

  async onSubmit($event: any): Promise<void> {
    this.phunkBoxError = false;
    this.phunkBoxLoading = true;

    try {
      const addressInput  = this.phunkBox?.value?.addressInput?.toLowerCase();

      const is0x = addressInput?.startsWith('0x');
      const isEns = addressInput?.includes('.eth');
      const isAddress = is0x && this.web3Svc.verifyAddress(addressInput);
      const isTokenId = !is0x && Number(addressInput) > -1;

      if (!isEns && !isAddress && !isTokenId) throw new Error('Invalid Search Parameters');

      if (isTokenId) {
        const number = Number(addressInput)
        if (number < 0 || number > 9999) {
          throw new Error('Invalid Search Parameters');
        }
        await this.router.navigate(['/', 'details', addressInput]);
        this.phunkBoxLoading = false;
      } else {
        let address = addressInput;
        if (isEns) address = await this.web3Svc.getEnsOwner(addressInput);
        else address = this.web3Svc.verifyAddress(addressInput);
        if (address) await this.router.navigate(['/', 'owned'], { queryParams: { address }});
        else throw new Error('Invalid Search Parameters');
      }

      // console.log({ isEns, isAddress, isTokenId, possibleHashId });
      let type = isEns ? 'ens' : isAddress ? 'address' : isTokenId ? 'tokenId' : 'unknown';

      this.addSearchHistory({ type, value: addressInput });

      //this.store.dispatch(appStateActions.addSearchHistory({ item: { type, value: addressInput } }));
      this.searchHistoryActive = false

    } catch (error) {
      console.log(error);

      this.phunkBoxError = true;
      this.phunkBox.reset();
      this.platformService.setTimeout(() => this.phunkBoxError = false, 5000);
    } finally {
      this.phunkBoxLoading = false;
      this.unfocusInput();
    }
  }

  addSearchHistory(item: HistoryItem) {
    const existingHistoryStr = this.platformService.getLocalStorageItem('searchHistory');
    const existingHistory = existingHistoryStr ? JSON.parse(existingHistoryStr) : [];
    const newHistory = [item, ...existingHistory.slice(0, 9)];
    this.searchHistory = newHistory.filter((historyItem, index, self) =>
      index === self.findIndex(h => h.type === historyItem.type && h.value === historyItem.value)
    );
    this.platformService.setLocalStorageItem('searchHistory', JSON.stringify(this.searchHistory));
  }

  async onFocus($event: any): Promise<void> {
    // console.log('onFocus', $event);
    if (!this.searchHistory.length) return;
    this.searchHistoryActive = true;
  }

  onBlur($event: any): void {
    // this.unfocusInput();
  }

  selectHistoryItem(item: HistoryItem): void {
    // console.log('selectHistoryItem', item);
    this.phunkBox.setValue({ addressInput: item.value });
    this.onSubmit(null);
  }

  removeHistoryItem(item: HistoryItem): void {
    this.searchHistory = this.searchHistory.filter(h => h.value !== item.value);
    this.platformService.setLocalStorageItem('searchHistory', JSON.stringify(this.searchHistory));
  }

  clearSearchHistory(): void {
    this.searchHistory = []
    this.platformService.setLocalStorageItem('searchHistory', JSON.stringify(this.searchHistory));
  }

  clearInput(): void {
    // console.log('clearInput');
    this.phunkBox.reset();
    this.searchInput.nativeElement.focus();
  }

  unfocusInput(): void {
    this.searchInput.nativeElement.blur();
    this.searchHistoryActive = false;
  }
}

